// Catálogo de comandos do Traccar — nomes, parâmetros e descrições.
//
// Nada aqui é inventado: os tipos de comando (chaves deste arquivo) e seus
// parâmetros foram extraídos do próprio Traccar 6.14.5 rodando neste
// ambiente — decompilação de `org.traccar.model.Command` (constantes
// TYPE_*/KEY_*) e do bundle real do frontend oficial do Traccar
// (`BaseCommandView-*.js`, que define exatamente quais atributos cada tipo
// de comando aceita). Os rótulos em português são os mesmos do arquivo de
// tradução oficial `pt_BR.json` que acompanha essa instalação do Traccar.
//
// A API do Traccar (`GET /api/commands/types?deviceId=`) já filtra pra só
// devolver os tipos que o protocolo do dispositivo realmente suporta — este
// arquivo só decora esses tipos com nome/descrição/parâmetros pra exibição;
// nunca decide sozinho quais comandos existem para um equipamento.

export type CommandParamType = "string" | "number" | "boolean";

export interface CommandParamDef {
  key: string;
  label: string;
  type: CommandParamType;
}

// org.traccar.model.Command TYPE_* -> rótulo (pt_BR.json do Traccar).
export const TRACCAR_COMMAND_LABELS: Record<string, string> = {
  custom: "Comando personalizado",
  deviceIdentification: "Identificação do dispositivo",
  positionSingle: "Solicitar localização",
  positionPeriodic: "Atualização periódica",
  positionStop: "Parar atualização",
  engineStop: "Bloquear motor",
  engineResume: "Desbloquear motor",
  alarmArm: "Ativar alarme",
  alarmDisarm: "Desativar alarme",
  alarmDismiss: "Ignorar alarme",
  setTimezone: "Definir fuso horário",
  requestPhoto: "Pegar foto",
  videoStart: "Iniciar vídeo",
  videoStop: "Parar vídeo",
  powerOff: "Desligar o dispositivo",
  rebootDevice: "Reiniciar rastreador",
  factoryReset: "Restauração de fábrica",
  sendSms: "Enviar SMS",
  sendUssd: "Enviar USSD",
  sosNumber: "Definir número SOS",
  silenceTime: "Definir tempo no silencioso",
  setPhonebook: "Definir lista telefônica",
  message: "Mensagem",
  voiceMessage: "Mensagem de voz",
  outputControl: "Controle de saída (buzzer/bloqueio)",
  voiceMonitoring: "Monitoramento de voz",
  setAgps: "Definir AGPS",
  setIndicator: "Definir indicador",
  configuration: "Configuração",
  getVersion: "Obter versão",
  firmwareUpdate: "Atualizar firmware",
  setConnection: "Definir conexão",
  setOdometer: "Definir odômetro",
  getModemStatus: "Obter status do modem",
  getDeviceStatus: "Obter status do dispositivo",
  setSpeedLimit: "Definir limite de velocidade",
  modePowerSaving: "Modo de economia de energia",
  modeDeepSleep: "Modo de sono profundo",
  alarmGeofence: "Definir alarme de geocerca",
  alarmBattery: "Definir alarme de bateria",
  alarmSos: "Definir alarme SOS",
  alarmRemove: "Definir remoção de alarme",
  alarmClock: "Definir horário de alarme",
  alarmSpeed: "Definir velocidade de alarme",
  alarmFall: "Definir alarme de queda",
  alarmVibration: "Definir alarme de vibração",
};

// Descrições curtas em português — texto de apoio nosso (o Traccar não
// expõe descrição por tipo via API), mantendo o sentido oficial de cada
// comando.
export const TRACCAR_COMMAND_DESCRIPTIONS: Record<string, string> = {
  custom: "Envia um texto bruto no protocolo do próprio rastreador.",
  deviceIdentification: "Solicita a identificação do dispositivo.",
  positionSingle: "Pede uma posição GPS imediata ao rastreador.",
  positionPeriodic: "Configura o envio periódico de posições.",
  positionStop: "Interrompe o envio periódico de posições.",
  engineStop: "Interrompe o motor do veículo remotamente.",
  engineResume: "Libera o motor do veículo remotamente.",
  alarmArm: "Ativa o alarme do veículo.",
  alarmDisarm: "Desativa o alarme do veículo.",
  alarmDismiss: "Ignora/silencia o alarme atual.",
  setTimezone: "Ajusta o fuso horário configurado no rastreador.",
  requestPhoto: "Solicita a captura de uma foto (equipamentos com câmera).",
  videoStart: "Inicia a transmissão de vídeo (equipamentos com câmera).",
  videoStop: "Para a transmissão de vídeo.",
  powerOff: "Desliga o dispositivo remotamente.",
  rebootDevice: "Reinicia o equipamento instalado no veículo.",
  factoryReset: "Restaura o dispositivo para as configurações de fábrica.",
  sendSms: "Faz o rastreador enviar um SMS para o número informado.",
  sendUssd: "Faz o rastreador enviar um código USSD para a operadora.",
  sosNumber: "Cadastra um número de telefone para chamadas de SOS.",
  silenceTime: "Define o intervalo de silêncio do rastreador.",
  setPhonebook: "Atualiza a lista telefônica salva no dispositivo.",
  message: "Envia uma mensagem de texto para o dispositivo.",
  voiceMessage: "Envia uma mensagem de voz para o dispositivo.",
  outputControl: "Aciona uma saída do rastreador (ex.: buzzer, bloqueio de combustível).",
  voiceMonitoring: "Ativa/desativa o monitoramento de voz (escuta ambiente).",
  setAgps: "Ativa/desativa o uso de AGPS para fixar posição mais rápido.",
  setIndicator: "Ajusta o indicador luminoso do equipamento.",
  configuration: "Envia uma configuração bruta para o dispositivo.",
  getVersion: "Solicita a versão de firmware do dispositivo.",
  firmwareUpdate: "Inicia a atualização de firmware do dispositivo.",
  setConnection: "Define o servidor e a porta para os quais o dispositivo deve reportar.",
  setOdometer: "Ajusta o valor do odômetro registrado no dispositivo.",
  getModemStatus: "Solicita o status do modem celular.",
  getDeviceStatus: "Solicita o status geral do dispositivo.",
  setSpeedLimit: "Define o limite de velocidade configurado no dispositivo.",
  modePowerSaving: "Ativa/desativa o modo de economia de energia.",
  modeDeepSleep: "Ativa/desativa o modo de sono profundo.",
  alarmGeofence: "Define o raio do alarme de geocerca interno do dispositivo.",
  alarmBattery: "Ativa/desativa o alarme de bateria fraca.",
  alarmSos: "Ativa/desativa o alarme de SOS.",
  alarmRemove: "Ativa/desativa o alarme de remoção do equipamento.",
  alarmClock: "Define um horário de alarme no dispositivo.",
  alarmSpeed: "Define a velocidade que dispara o alarme de excesso de velocidade.",
  alarmFall: "Ativa/desativa o alarme de queda.",
  alarmVibration: "Ativa/desativa o alarme de vibração.",
};

// org.traccar.model.Command KEY_* -> rótulo (pt_BR.json do Traccar).
const PARAM_LABELS: Record<string, string> = {
  data: "Dados",
  frequency: "Frequência (segundos)",
  timezone: "Fuso horário",
  message: "Mensagem",
  enable: "Habilitar",
  index: "Índice",
  phone: "Número de telefone",
  server: "Servidor",
  port: "Porta",
  radius: "Raio (metros)",
};

function param(key: string, type: CommandParamType): CommandParamDef {
  return { key, label: PARAM_LABELS[key] ?? key, type };
}

// Tipo -> parâmetros aceitos, extraído de BaseCommandView do Traccar
// (mesmo mapeamento usado pela interface oficial). Tipos ausentes aqui não
// têm parâmetro nenhum — só o tipo é enviado.
export const TRACCAR_COMMAND_PARAMS: Record<string, CommandParamDef[]> = {
  custom: [param("data", "string")],
  positionPeriodic: [param("frequency", "number")],
  setTimezone: [param("timezone", "string")],
  sendSms: [param("phone", "string"), param("message", "string")],
  message: [param("message", "string")],
  sendUssd: [param("phone", "string")],
  sosNumber: [param("index", "number"), param("phone", "string")],
  silenceTime: [param("data", "string")],
  setPhonebook: [param("data", "string")],
  voiceMessage: [param("data", "string")],
  outputControl: [param("index", "number"), param("data", "string")],
  voiceMonitoring: [param("enable", "boolean")],
  setAgps: [param("enable", "boolean")],
  setIndicator: [param("data", "string")],
  configuration: [param("data", "string")],
  setConnection: [param("server", "string"), param("port", "number")],
  setOdometer: [param("data", "string")],
  modePowerSaving: [param("enable", "boolean")],
  modeDeepSleep: [param("enable", "boolean")],
  alarmGeofence: [param("radius", "number")],
  alarmBattery: [param("enable", "boolean")],
  alarmSos: [param("enable", "boolean")],
  alarmRemove: [param("enable", "boolean")],
  alarmClock: [param("data", "string")],
  alarmSpeed: [param("data", "string")],
  alarmFall: [param("enable", "boolean")],
  alarmVibration: [param("data", "string")],
};

// Identificador especial da opção "comando personalizado" no seletor —
// corresponde exatamente ao tipo real `custom` do Traccar, só que renderiza
// o parâmetro `data` como textarea em vez de campo de texto simples.
export const CUSTOM_COMMAND_TYPE = "custom";

export function commandLabel(type: string): string {
  return TRACCAR_COMMAND_LABELS[type] ?? type;
}

export function commandDescription(type: string): string {
  return TRACCAR_COMMAND_DESCRIPTIONS[type] ?? "Comando enviado conforme o protocolo do dispositivo.";
}

export function commandParams(type: string): CommandParamDef[] {
  return TRACCAR_COMMAND_PARAMS[type] ?? [];
}

export function defaultAttributesFor(type: string): Record<string, string | number | boolean> {
  const attrs: Record<string, string | number | boolean> = {};
  for (const p of commandParams(type)) {
    attrs[p.key] = p.type === "boolean" ? false : p.type === "number" ? 0 : "";
  }
  return attrs;
}
